

























































import { fromNullable, none, Option } from 'fp-ts/lib/Option'
import _ from 'lodash'
import { Mixins } from 'vue-mixin-decorator'
import { Component, Prop } from 'vue-property-decorator'

import AjaxInput from '@/components/atoms/Input.vue'
import PageTransitionLink from '@/components/atoms/PageTransitionLink.vue'
import SearchKeyword from '@/components/organisms/search/SearchKeyword.vue'
import SlideMenu from '@/components/organisms/slide-menu/SlideMenu.vue'
import { log } from '@/log'
import ApiBrand from '@/models/api/brand'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { CartScope } from '@/models/cart/group/scope'
import { getInstanceContext } from '@/page/context/instance'
import { hostPageContext } from '@/page/context/page'
import { ContextToCreateUrl } from '@/page/url/factory/url-factory'
import { LocationGroupStore } from '@/routing/location-group'
import { BrandLogo, BrandService } from '@/services/api/brand'
import { BrandRouteResolver } from '@/util/brandRouteResolver'
import { InstanceResolver } from '@/util/instanceResolver'
import {
  initializeSlideMenuFreeArea,
  initialSlideMenuState,
  setPosition,
  SlideMenuState,
  toggleSlideMenu,
} from '@spa/components/organisms/models/ec-header/slide-menu-state'
import { PluginMixin } from '@spa/mixins/plugin'
import { InfoService } from '@spa/services/api/info'
import { CookieService } from '@spa/services/cookie'
import { Either, Right } from 'fp-ts/lib/Either'

const spaContext = {
  instance: getInstanceContext(),
  page: hostPageContext,
}

@Component({
  components: {
    PageTransitionLink,
    AjaxInput,
    SearchKeyword,
    SlideMenu,
  },
})
export default class EcHeader extends Mixins<PluginMixin>(PluginMixin) {
  $refs: {
    ecHeader: HTMLElement,
  }

  @Prop({ default: false })
  readonly isMediaPage: boolean

  @Prop({ default: () => spaContext })
  readonly context: ContextToCreateUrl

  versioning: string | null = null

  shouldDisplayVersioning: boolean = false

  brandService = new BrandService()

  // brand情報として、特に「header用の」情報としてまとめたい
  brand: Option<ApiBrand> = none
  logo: BrandLogo | null = null
  cartScope: CartScope | null = null

  slideMenuState: SlideMenuState = initialSlideMenuState

  loading: boolean = true

  async created() {
    const maybeBrandName = fromNullable(
      // this.$route.params.brand_english_name はブランドページ以外ではundefinedとなり、その時はnullを返す
      BrandRouteResolver.resolveBrandFromPathElement(this.$route.params.brand_english_name) || null
    )

    const requests: Array<Promise<any>> = [
      InfoService.versioning().then(result => result.map((version) => {
        this.versioning = version.fullText
        this.shouldDisplayVersioning = version.display
      })),

      maybeBrandName.fold(
        Promise.resolve<Either<Error, Option<ApiBrand>>>(new Right(none)),
        brandName => this.brandService.findByEnglishName(brandName)
      ).then(result => result.map((maybeBrand) => {
        this.brand = maybeBrand
      })),

      maybeBrandName.fold(
        Promise.resolve<Either<Error, BrandLogo | null>>(new Right(null)),
        brandName => this.brandService.findLogoImage(brandName)
      ).then(result => result.fold(
        error => log.error(error),
        (brandLogo) => {
          this.logo = brandLogo
        }
      )),

      /**
       * 特定のブランドに属さない場合カートスコープはnullとなる
       * @see https://github.com/Everforth/growthpalette-front/issues/7582#issuecomment-2105565546
       */
      maybeBrandName.fold(
        Promise.resolve<Either<Error, CartScope | null>>(new Right(null)),
        brandName => this.brandService.findCartScope(brandName)
      ).then(result => result.map((scope: CartScope | null) => {
        this.cartScope = scope
      })),

      maybeBrandName.fold(
        Promise.resolve(),
        (brandName) => {
          /**
           * カート点数取得
           * ホテラバのカート画面で2重にカートを取得しないための緊急対応として、
           * カート画面であればカートロードをdispatchしないようにする
           * 本当はもっとちゃんと整理したい
           * @see https://github.com/Everforth/growthpalette-front/issues/8214
           */
          if (this.isCartPage || CookieService.isGuest()) {
            return Promise.resolve()
          }

          return this.$store.dispatch('cart/loadCountByBrand', { brandName })
        }
      ),
    ]

    await Promise.all(requests)

    this.loading = false

    this.slideMenuState = await initializeSlideMenuFreeArea(
      this.slideMenuState,
      this.brand.isSome(),
      this.brand.map(apiBrand => apiBrand.englishName).getOrElse('')
    )
  }

  get showTransitionLinks(): boolean {
    return InstanceResolver.isSingle() || !this.isMediaPage
  }

  get isCartPage(): boolean {
    return LocationGroupStore.getInstance().locationIsGroupOf(['cart'])
  }

  /**
   * デフォルトでは class="header__my-color-logo" に指定されたbackground-imageを使用.
   *
   * ブランド配下の場合、ブランドに画像が登録されていればその画像を使用する.
   * 画像未登録の場合は、↑のデフォルト値が使用される.
   */
  get logoStyle(): object {
    if (this.loading) {
      return {
        'background-image': 'none',
      }
    }

    if (this.brand.isSome() && this.logo) {
      return {
        'background-image': `url(${this.logo.url})`,
      }
    }

    return {}
  }

  get cartItemTotalCountUnderBrand(): number {
    if (this.brand.isNone()) {
      return 0
    }

    return this.$store.getters['cart/currentCartItemCount']
  }

  get isLogined() {
    return CookieService.isLogined()
  }

  get topUrl(): string {
    if (InstanceResolver.isSingle()) {
      return '/'
    }

    return this.brand.fold(
      '/',
      apiBrand => `/${apiBrand.englishName}`
    )
  }

  get brandStyleClass() {
    return this.brand.fold(
      'header__no-brand',
      apiBrand => `header__brand-${apiBrand.englishName}`
    )
  }

  get cartLink(): string {
    if (InstanceResolver.isSingle()) {
      return '/cart'
    }

    if (this.brand.isNone() || !this.cartScope) {
      return '/carts'
    }

    return `/${this.cartScope.kind.toString()}/${this.cartScope.value.toString()}/cart`
  }

  get showSearchKeyword(): boolean {
    return this.brand.isSome() && !this.loading
  }

  toggleSlideMenu () {
    this.slideMenuState = setPosition(this.slideMenuState, {
      top: this.$refs.ecHeader.getBoundingClientRect().bottom,
    })
    this.slideMenuState = toggleSlideMenu(this.slideMenuState)
  }

  get hamburgerMenuClass(): string {
    return this.slideMenuState.visibility ?
      'header__toggle-hamburger-menu--close' :
      'header__toggle-hamburger-menu'
  }

  get behaviorConfig(): BehaviorConfig {
    return BehaviorConfig.createFromBrand(this.brand.toNullable())
  }
}
